/**
 * View-model hook for the swimlane header row: owns the minimap scrubber
 * wiring (progress tracking + headroom anchor reset on scrub) and assembles
 * the memoized `TimelineHeaderProps` object for `TimelineSwimLanes`.
 */

import { useCallback, useMemo, type RefObject } from "react";

import { useScrubberProgress } from "@tsmono/react/hooks";

import { type TimelineHeaderProps } from "../timeline/components";
import {
  type MultiTimelineNav,
  type TimelineMinimapData,
  type TimelineViewStack,
  type UseTimelineConfigResult,
} from "../timeline/hooks";

export interface UseSwimlaneHeaderOptions {
  /** The transcript's scroll container (scrub target). */
  scrollRef: RefObject<HTMLDivElement | null>;
  /** Called on header click to scroll the view to the top. */
  onScrollToTop?: () => void;
  /** Headroom anchor reset, fired (debounced) on scrub so the swimlane
   *  doesn't collapse/expand while the scrubber drives the scroll. */
  onHeadroomResetAnchor?: (debounce?: boolean) => void;
  /** Timeline config for the options popover. */
  timelineConfig?: UseTimelineConfigResult;
  /** Number of utility agents elided from display (shows the header indicator
   *  when > 0 and utility agents are toggled off). */
  hiddenUtilityCount?: number;
  /** Minimap data (root time mapping + selection). */
  minimap?: TimelineMinimapData;
  /** Multi-timeline navigation. */
  multiTimeline?: MultiTimelineNav;
  /** Punch-down view navigation. */
  views?: TimelineViewStack;
}

export function useSwimlaneHeader(
  options: UseSwimlaneHeaderOptions
): TimelineHeaderProps {
  const {
    scrollRef,
    onScrollToTop,
    onHeadroomResetAnchor,
    timelineConfig,
    hiddenUtilityCount,
    minimap,
    multiTimeline,
    views,
  } = options;

  const [scrubberProgress, scrubTo] = useScrubberProgress(scrollRef);

  const handleScrub = useCallback(
    (progress: number) => {
      onHeadroomResetAnchor?.(true);
      scrubTo(progress);
    },
    [onHeadroomResetAnchor, scrubTo]
  );

  return useMemo(
    () => ({
      onScrollToTop,
      minimap,
      scrubberProgress,
      onScrub: handleScrub,
      timelineConfig,
      hiddenUtilityCount,
      multiTimeline,
      views,
    }),
    [
      onScrollToTop,
      minimap,
      scrubberProgress,
      handleScrub,
      timelineConfig,
      hiddenUtilityCount,
      multiTimeline,
      views,
    ]
  );
}
